import { useAppUserDetailsFromJwt } from 'common-react/auth/utils/hooks/useAppUserDetailsFromJwt/useAppUserDetailsFromJwt';
import { FullPageLoader } from 'components/BouncingLoader/FullPageLoader';
import { NoPermissionErrorState } from 'components/ErrorStates/NoPermissionErrorState/NoPermissionErrorState';
import { NoSitesErrorState } from 'components/ErrorStates/NoSitesErrorState/NoSitesErrorState';
import { SitesFetchingErrorState } from 'components/ErrorStates/NoSitesErrorState/SitesFetchingErrorState';
import { Permissions } from 'context/constants';
import { usePlutoAvailabilityApiQuery } from 'hooks/useQuery/plutoAvailabilityApi';
import { Availability } from 'pages/Availability/Availability';
import { GET_SITES } from 'pages/Availability/queries';
import { Site } from 'pages/Availability/types';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useStore } from 'store';

export const AvailabilityWrapper = () => {
  const navigate = useNavigate();
  const { company, site } = useParams();
  const {
    companyData,
    setSelectedSiteData,
    resetPersistedSiteAvailabilityFormData,
    resetPersistedAssetAvailabilityFormData,
  } = useStore((state) => ({
    companyData: state.companyData,
    setSelectedSiteData: state.setSelectedSiteData,
    resetPersistedSiteAvailabilityFormData:
      state.resetPersistedSiteAvailabilityFormData,
    resetPersistedAssetAvailabilityFormData:
      state.resetPersistedAssetAvailabilityFormData,
  }));

  const { permissions } = useAppUserDetailsFromJwt<Permissions>();

  const hasPermission = permissions?.includes(Permissions.EDIT_AVAILABILITY);

  // Ensure companyData corresponds to current route param before querying
  const isCompanyReady =
    !!company &&
    companyData?.node?.id === company &&
    !!companyData?.node?.sourceName &&
    !!companyData?.node?.sourceId;

  const shouldSkipSitesQuery = !hasPermission || !isCompanyReady;

  const { data, loading, error } = usePlutoAvailabilityApiQuery<{
    sites: Array<Site>;
  }>(GET_SITES, {
    skip: shouldSkipSitesQuery,
    variables: {
      filter: {
        company: {
          sourceName: { eq: companyData?.node?.sourceName },
          sourceId: { eq: companyData?.node?.sourceId },
        },
      },
      sorting: [
        {
          field: 'name',
          direction: 'ASC',
        },
      ],
    },
  });

  // If no site is in the URL, auto-navigate to the first site once loaded (does not override user selection)
  useEffect(() => {
    if (!isCompanyReady) return;
    if (site) return;
    const firstSite = data?.sites?.[0];
    if (firstSite) {
      navigate(`/${company}/availability/${firstSite.id}`, { replace: true });
      setSelectedSiteData(firstSite);
    }
    resetPersistedSiteAvailabilityFormData();
    resetPersistedAssetAvailabilityFormData();
  }, [isCompanyReady, data?.sites, site, company, navigate]);

  if (!hasPermission) {
    return <NoPermissionErrorState />;
  }

  if (!isCompanyReady || loading) {
    return <FullPageLoader />;
  }

  if (error) {
    return <SitesFetchingErrorState />;
  }

  if (!data?.sites?.length) {
    return <NoSitesErrorState />;
  }

  const sites: Array<Site> = data.sites;

  return <Availability sites={sites} />;
};
